import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  FunnelChart, Funnel, LabelList, Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLOW_COLORS = {
  free_lead: "#22c55e",
  purchase: "#3b82f6",
  nurture: "#a855f7",
  abandoned_cart: "#ef4444",
};

const FUNNEL_COLORS = ["#22c55e", "#3b82f6", "#eab308", "#f97316", "#ef4444"];

function StatCard({ label, value, sub, color = "text-white" }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-zinc-600 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function SectionHeader({ title }) {
  return <h2 className="text-lg font-bold text-white mb-4 mt-8 border-b border-zinc-800 pb-2">{title}</h2>;
}

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [perEmail, setPerEmail] = useState([]);
  const [funnels, setFunnels] = useState({});
  const [revenue, setRevenue] = useState({ attributed: [], unattributed_revenue: 0 });
  const [recovery, setRecovery] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [leadsPurchases, setLeadsPurchases] = useState({ rows: [], stats: {} });
  const [lpSearch, setLpSearch] = useState("");
  const [lpFilter, setLpFilter] = useState("all");
  const [journeyEmail, setJourneyEmail] = useState("");
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const navigate = useNavigate();

  const token = localStorage.getItem("admin_token");
  // Memoize headers so fetchData identity is stable and useEffect doesn't loop
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    if (!token) { navigate("/admin"); return; }
    try {
      const [ov, pe, fn, rv, rc, sl, ev, lp] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, { headers }),
        axios.get(`${API}/admin/analytics/per-email`, { headers }),
        axios.get(`${API}/admin/analytics/funnel`, { headers }),
        axios.get(`${API}/admin/analytics/revenue`, { headers }),
        axios.get(`${API}/admin/analytics/recovery`, { headers }),
        axios.get(`${API}/admin/analytics/subject-lines`, { headers }),
        axios.get(`${API}/admin/analytics/recent-events`, { headers }),
        axios.get(`${API}/admin/leads-purchases`, { headers }),
      ]);
      setOverview(ov.data);
      setPerEmail(pe.data.emails);
      setFunnels(fn.data.funnels);
      setRevenue(rv.data);
      setRecovery(rc.data);
      setSubjects(sl.data.subject_lines);
      setRecentEvents(ev.data.events);
      setLeadsPurchases(lp.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const searchJourney = async () => {
    if (!journeyEmail) return;
    try {
      const { data } = await axios.get(`${API}/admin/analytics/user-journey?email=${encodeURIComponent(journeyEmail)}`, { headers });
      setJourney(data);
    } catch (err) {
      console.warn('[AdminDashboard] user-journey fetch failed:', err?.message || err);
      setJourney(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_name");
    navigate("/admin");
  };

  // Change password state
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg(""); setPwError("");
    if (newPw.length < 8) { setPwError("Password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { setPwError("Passwords do not match"); return; }
    setPwLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/change-password`, { current_password: currentPw, new_password: newPw }, { headers });
      setPwMsg(data.message);
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      setPwError(err.response?.data?.detail || "Failed to change password");
    } finally { setPwLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-zinc-500 text-lg">Loading analytics...</div>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "leads", label: "Leads & Purchases" },
    { id: "performance", label: "Email Performance" },
    { id: "revenue", label: "Revenue" },
    { id: "funnels", label: "Funnels" },
    { id: "subjects", label: "Subject Lines" },
    { id: "recovery", label: "Cart Recovery" },
    { id: "journey", label: "User Journey" },
    { id: "events", label: "Live Feed" },
    { id: "settings", label: "Settings" },
  ];

  const refreshLeadsPurchases = async () => {
    try {
      const params = new URLSearchParams();
      if (lpSearch) params.set("search", lpSearch);
      if (lpFilter !== "all") params.set("filter_type", lpFilter);
      const { data } = await axios.get(`${API}/admin/leads-purchases?${params.toString()}`, { headers });
      setLeadsPurchases(data);
    } catch (err) {
      console.warn('[AdminDashboard] leads-purchases fetch failed:', err?.message || err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex justify-between items-center sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold">
            Paperwork<span className="text-amber-400">Depot</span>
            <span className="text-zinc-500 font-normal text-sm ml-3">Analytics</span>
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={fetchData} className="text-zinc-400 hover:text-white text-sm transition-colors" data-testid="refresh-btn">Refresh</button>
          <button onClick={handleLogout} className="text-zinc-500 hover:text-red-400 text-sm transition-colors" data-testid="logout-btn">Logout</button>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="bg-zinc-900/50 border-b border-zinc-800 px-6 overflow-x-auto">
        <div className="flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                tab === t.id ? "border-amber-400 text-amber-400" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* OVERVIEW TAB */}
        {tab === "overview" && overview && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard label="Emails Sent" value={overview.total_sent} />
              <StatCard label="Delivered" value={overview.total_delivered} sub={`${overview.delivery_rate}% rate`} />
              <StatCard label="Opened" value={overview.total_opened} sub={`${overview.open_rate}% rate`} color="text-green-400" />
              <StatCard label="Clicked" value={overview.total_clicked} sub={`${overview.click_rate}% rate`} color="text-blue-400" />
              <StatCard label="Bounced" value={overview.total_bounced} color="text-red-400" />
              <StatCard label="Total Leads" value={overview.total_leads} color="text-purple-400" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <StatCard label="Email Revenue" value={`$${overview.attributed_revenue.toLocaleString()}`} sub={`${overview.attributed_orders} orders`} color="text-amber-400" />
              <StatCard label="Total Revenue" value={`$${overview.total_revenue.toLocaleString()}`} />
              <StatCard label="Cart Recovery Rate" value={`${overview.recovery_rate}%`} sub={`${overview.recovered_count} recovered`} color="text-green-400" />
              <StatCard label="Abandoned Emails To" value={overview.abandoned_cart_emails_sent_to} />
            </div>
          </div>
        )}

        {/* LEADS & PURCHASES TAB */}
        {tab === "leads" && (
          <div>
            {/* Stat Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard label="Free Guide Leads" value={leadsPurchases.stats?.total_leads || 0} color="text-purple-400" />
              <StatCard label="Paid Purchases" value={leadsPurchases.stats?.total_purchases_paid || 0} color="text-green-400" />
              <StatCard label="Pending / Failed" value={leadsPurchases.stats?.total_pending || 0} color="text-amber-400" />
              <StatCard label="Total Revenue" value={`$${(leadsPurchases.stats?.total_revenue || 0).toLocaleString()}`} color="text-amber-400" />
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4 items-stretch sm:items-center">
              <input
                type="text"
                placeholder="Search by email..."
                value={lpSearch}
                onChange={(e) => setLpSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && refreshLeadsPurchases()}
                className="bg-zinc-900 border border-zinc-800 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 flex-1 max-w-md"
                data-testid="leads-search-input"
              />
              <div className="flex gap-1">
                {[
                  { v: "all", l: "All" },
                  { v: "leads", l: "Leads only" },
                  { v: "purchases", l: "Purchases only" },
                ].map((f) => (
                  <button
                    key={f.v}
                    onClick={() => { setLpFilter(f.v); }}
                    className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                      lpFilter === f.v ? "bg-amber-500 text-black" : "bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800"
                    }`}
                    data-testid={`leads-filter-${f.v}`}
                  >
                    {f.l}
                  </button>
                ))}
              </div>
              <button
                onClick={refreshLeadsPurchases}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-medium rounded-lg border border-zinc-700"
                data-testid="leads-refresh-btn"
              >
                Apply
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto bg-zinc-900 border border-zinc-800 rounded-xl">
              <table className="w-full text-sm" data-testid="leads-purchases-table">
                <thead>
                  <tr className="text-zinc-500 text-xs uppercase border-b border-zinc-800">
                    <th className="text-left py-3 px-4">Type</th>
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Product</th>
                    <th className="text-right py-3 px-4">Amount</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Email Sent</th>
                    <th className="text-left py-3 px-4">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leadsPurchases.rows?.map((r, i) => {
                    const isLead = r.type === "lead";
                    const stableKey = `${r.type}-${r.session_id || r.email}-${r.created_at || i}`;
                    const statusColor = r.status === "paid" ? "text-green-400 bg-green-950"
                      : r.status === "captured" ? "text-purple-400 bg-purple-950"
                      : r.status === "initiated" || r.status === "pending" ? "text-amber-400 bg-amber-950"
                      : "text-zinc-400 bg-zinc-800";
                    return (
                      <tr key={stableKey} className="border-b border-zinc-800/50 hover:bg-zinc-900/70" data-testid={`leads-row-${i}`}>
                        <td className="py-3 px-4">
                          <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${isLead ? "bg-purple-950 text-purple-300" : "bg-blue-950 text-blue-300"}`}>
                            {isLead ? "Lead" : "Purchase"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-zinc-200 font-mono text-xs">{r.email || "—"}</td>
                        <td className="py-3 px-4 text-zinc-400 max-w-md truncate">{r.product_title}</td>
                        <td className="text-right py-3 px-4 text-zinc-300">
                          {r.amount > 0 ? `$${r.amount.toFixed(2)}` : "—"}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${statusColor}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {isLead ? (
                            <span className={r.downloaded ? "text-green-400 text-xs" : "text-zinc-500 text-xs"}>
                              {r.downloaded ? `Downloaded ${r.download_count}x` : "Sent (no DL yet)"}
                            </span>
                          ) : (
                            <span className={r.email_sent ? "text-green-400 text-xs" : "text-zinc-500 text-xs"}>
                              {r.email_sent ? "Receipt sent" : "—"}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-zinc-500 text-xs">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {(!leadsPurchases.rows || leadsPurchases.rows.length === 0) && (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-zinc-600">No leads or purchases yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PERFORMANCE TAB */}
        {tab === "performance" && (
          <div>
            <SectionHeader title="Per-Email Performance" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="performance-table">
                <thead>
                  <tr className="text-zinc-500 text-xs uppercase border-b border-zinc-800">
                    <th className="text-left py-3 px-3">Email</th>
                    <th className="text-right py-3 px-2">Sent</th>
                    <th className="text-right py-3 px-2">Delivered</th>
                    <th className="text-right py-3 px-2">Opened</th>
                    <th className="text-right py-3 px-2">Clicked</th>
                    <th className="text-right py-3 px-2">Open %</th>
                    <th className="text-right py-3 px-2">Click %</th>
                    <th className="text-right py-3 px-2">Revenue</th>
                    <th className="text-right py-3 px-2">Conversions</th>
                  </tr>
                </thead>
                <tbody>
                  {perEmail.map((e) => {
                    const flowKey = e.drip_type.split("_").slice(0, -1).join("_").replace("abandoned_cart", "abandoned_cart");
                    const flow = e.drip_type.startsWith("abandoned") ? "abandoned_cart"
                      : e.drip_type.startsWith("free") ? "free_lead"
                      : e.drip_type.startsWith("purchase") ? "purchase"
                      : "nurture";
                    const dotColor = FLOW_COLORS[flow] || "#888";
                    return (
                      <tr key={e.drip_type} className="border-b border-zinc-800/50 hover:bg-zinc-900/50">
                        <td className="py-3 px-3 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
                          <span className="text-zinc-300 text-xs">{e.label}</span>
                        </td>
                        <td className="text-right py-3 px-2 text-zinc-400">{e.sent}</td>
                        <td className="text-right py-3 px-2 text-zinc-400">{e.delivered}</td>
                        <td className="text-right py-3 px-2 text-green-400">{e.opened}</td>
                        <td className="text-right py-3 px-2 text-blue-400">{e.clicked}</td>
                        <td className="text-right py-3 px-2">
                          <span className={e.open_rate > 30 ? "text-green-400" : e.open_rate > 15 ? "text-amber-400" : "text-zinc-500"}>
                            {e.open_rate}%
                          </span>
                        </td>
                        <td className="text-right py-3 px-2">
                          <span className={e.click_rate > 5 ? "text-blue-400" : "text-zinc-500"}>
                            {e.click_rate}%
                          </span>
                        </td>
                        <td className="text-right py-3 px-2 text-amber-400 font-medium">{e.revenue > 0 ? `$${e.revenue}` : "-"}</td>
                        <td className="text-right py-3 px-2 text-zinc-400">{e.conversions || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* REVENUE TAB */}
        {tab === "revenue" && (
          <div>
            <SectionHeader title="Revenue Attribution" />
            <div className="grid grid-cols-2 gap-4 mb-6">
              <StatCard label="Email-Attributed Revenue" value={`$${revenue.attributed.reduce((s, r) => s + r.total_revenue, 0).toLocaleString()}`} color="text-amber-400" />
              <StatCard label="Unattributed Revenue" value={`$${revenue.unattributed_revenue.toLocaleString()}`} sub={`${revenue.unattributed_orders} orders`} />
            </div>
            {revenue.attributed.length > 0 ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenue.attributed} layout="vertical" margin={{ left: 180 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" tickFormatter={(v) => `$${v}`} stroke="#666" />
                    <YAxis type="category" dataKey="label" width={170} tick={{ fill: "#999", fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8 }}
                      formatter={(v) => [`$${v.toFixed(2)}`, "Revenue"]}
                    />
                    <Bar dataKey="total_revenue" fill="#eab308" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-zinc-600 text-sm">No attributed revenue yet. Revenue is attributed when a user clicks an email link and then makes a purchase.</p>
            )}
          </div>
        )}

        {/* FUNNELS TAB */}
        {tab === "funnels" && (
          <div>
            <SectionHeader title="Email Flow Funnels" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(funnels).map(([flow, data]) => (
                <div key={flow} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: FLOW_COLORS[flow] }}>
                    {flow.replace("_", " ")} Flow
                  </h3>
                  <div className="space-y-2">
                    {data.stages.map((stage, i) => {
                      const maxCount = data.stages[0].count || 1;
                      const pct = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
                      const dropoff = i > 0 && data.stages[i - 1].count > 0
                        ? Math.round((1 - stage.count / data.stages[i - 1].count) * 100)
                        : 0;
                      return (
                        <div key={stage.name} className="flex items-center gap-3">
                          <span className="text-zinc-500 text-xs w-20 text-right">{stage.name}</span>
                          <div className="flex-1 bg-zinc-800 rounded-full h-6 relative overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${Math.max(pct, 2)}%`, background: FUNNEL_COLORS[i] || "#666" }}
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                              {stage.count}
                            </span>
                          </div>
                          {i > 0 && dropoff > 0 && (
                            <span className="text-red-500 text-xs w-12">-{dropoff}%</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SUBJECT LINES TAB */}
        {tab === "subjects" && (
          <div>
            <SectionHeader title="Subject Lines Ranked by Open Rate" />
            <div className="space-y-2">
              {subjects.map((s, i) => (
                <div key={s.drip_type} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-4">
                  <span className="text-zinc-600 text-lg font-bold w-8 text-center">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{s.subject || "(no subject)"}</p>
                    <p className="text-zinc-600 text-xs">{s.label}</p>
                  </div>
                  <div className="flex gap-6 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-green-400 text-sm font-bold">{s.open_rate}%</p>
                      <p className="text-zinc-600 text-xs">Open</p>
                    </div>
                    <div className="text-center">
                      <p className="text-blue-400 text-sm font-bold">{s.click_rate}%</p>
                      <p className="text-zinc-600 text-xs">Click</p>
                    </div>
                    <div className="text-center">
                      <p className="text-zinc-400 text-sm font-bold">{s.sent}</p>
                      <p className="text-zinc-600 text-xs">Sent</p>
                    </div>
                  </div>
                </div>
              ))}
              {subjects.length === 0 && <p className="text-zinc-600 text-sm">No subject line data yet.</p>}
            </div>
          </div>
        )}

        {/* CART RECOVERY TAB */}
        {tab === "recovery" && recovery && (
          <div>
            <SectionHeader title="Abandoned Cart Recovery" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Abandoned Sessions" value={recovery.total_abandoned_sessions} />
              <StatCard label="Emails Sent" value={recovery.emails_sent} />
              <StatCard label="Recovered Purchases" value={recovery.recovered_purchases} color="text-green-400" />
              <StatCard label="Revenue Recovered" value={`$${recovery.recovered_revenue.toLocaleString()}`} color="text-amber-400" />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <StatCard label="Recovery Rate" value={`${recovery.recovery_rate}%`} sub={`${recovery.unique_recipients} unique recipients`} color="text-green-400" />
              <StatCard label="Cancelled (Paid Before Send)" value={recovery.cancelled_before_send} sub="Paid before first email sent" />
            </div>
            {recovery.time_to_purchase_samples.length > 0 && (
              <>
                <SectionHeader title="Time-to-Purchase Samples" />
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-zinc-500 text-xs uppercase border-b border-zinc-800">
                        <th className="text-left py-3 px-4">Email</th>
                        <th className="text-left py-3 px-4">Abandoned At</th>
                        <th className="text-left py-3 px-4">Purchased At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recovery.time_to_purchase_samples.map((s, i) => (
                        <tr key={i} className="border-b border-zinc-800/50">
                          <td className="py-2 px-4 text-zinc-400">{s.email}</td>
                          <td className="py-2 px-4 text-zinc-500">{new Date(s.abandoned_at).toLocaleString()}</td>
                          <td className="py-2 px-4 text-green-400">{new Date(s.purchased_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* USER JOURNEY TAB */}
        {tab === "journey" && (
          <div>
            <SectionHeader title="User Email Journey" />
            <div className="flex gap-3 mb-6">
              <input
                type="email"
                value={journeyEmail}
                onChange={(e) => setJourneyEmail(e.target.value)}
                placeholder="Enter customer email..."
                className="flex-1 bg-zinc-900 border border-zinc-800 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                onKeyDown={(e) => e.key === "Enter" && searchJourney()}
                data-testid="journey-email-input"
              />
              <button
                onClick={searchJourney}
                className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-6 py-3 rounded-lg text-sm transition-colors"
                data-testid="journey-search-btn"
              >
                Search
              </button>
            </div>
            {journey && (
              <div>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <StatCard label="Emails Sent" value={journey.total_emails_sent} />
                  <StatCard label="Events Tracked" value={journey.total_events} />
                  <StatCard label="Purchases" value={journey.total_purchases} color="text-green-400" />
                </div>
                {journey.pending_drips.length > 0 && (
                  <div className="bg-amber-950 border border-amber-800 rounded-xl p-4 mb-6">
                    <p className="text-amber-300 text-sm font-medium mb-2">Pending Drips ({journey.pending_drips.length})</p>
                    {journey.pending_drips.map((d, i) => (
                      <p key={i} className="text-amber-200 text-xs">{d.drip_type} — scheduled: {new Date(d.send_at).toLocaleString()}</p>
                    ))}
                  </div>
                )}
                <div className="space-y-1">
                  {journey.timeline.map((item, i) => {
                    const isEmail = item.type.startsWith("email_");
                    const isPurchase = item.type === "purchase";
                    const dotColor = isPurchase ? "#22c55e" : isEmail ? (item.type.includes("clicked") ? "#3b82f6" : item.type.includes("opened") ? "#eab308" : "#666") : "#888";
                    return (
                      <div key={i} className="flex items-start gap-3 py-2 border-l-2 pl-4 ml-2" style={{ borderColor: dotColor }}>
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: dotColor }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-zinc-300 text-sm">{item.label}</p>
                          {item.link && <p className="text-blue-400 text-xs truncate">{item.link}</p>}
                          {item.amount && <p className="text-green-400 text-xs font-medium">${item.amount}</p>}
                        </div>
                        <span className="text-zinc-600 text-xs flex-shrink-0">{item.timestamp ? new Date(item.timestamp).toLocaleString() : ""}</span>
                      </div>
                    );
                  })}
                  {journey.timeline.length === 0 && <p className="text-zinc-600 text-sm">No activity found for this email.</p>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* LIVE EVENTS FEED TAB */}
        {tab === "events" && (
          <div>
            <SectionHeader title="Recent Email Events" />
            <div className="space-y-1">
              {recentEvents.map((ev, i) => {
                const typeColors = {
                  delivered: "text-zinc-400", opened: "text-green-400", clicked: "text-blue-400",
                  bounced: "text-red-400", sent: "text-zinc-500", complained: "text-red-500",
                };
                return (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 flex items-center gap-4">
                    <span className={`text-xs font-bold uppercase w-20 ${typeColors[ev.event_type] || "text-zinc-500"}`}>
                      {ev.event_type}
                    </span>
                    <span className="text-zinc-400 text-sm flex-1 truncate">{ev.to}</span>
                    <span className="text-zinc-600 text-xs flex-shrink-0">{ev.drip_type}</span>
                    <span className="text-zinc-700 text-xs flex-shrink-0">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ""}</span>
                  </div>
                );
              })}
              {recentEvents.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-zinc-600 text-sm">No events received yet.</p>
                  <p className="text-zinc-700 text-xs mt-2">Configure your Resend webhook URL to: <code className="text-amber-500">{API}/admin/webhooks/resend</code></p>
                </div>
              )}
            </div>
          </div>
        )}
        {/* SETTINGS TAB */}
        {tab === "settings" && (
          <div>
            <SectionHeader title="Change Password" />
            <form onSubmit={handleChangePassword} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-lg space-y-4" data-testid="change-password-form">
              {pwMsg && (
                <div className="bg-green-950 border border-green-800 text-green-300 text-sm px-4 py-3 rounded-lg" data-testid="pw-change-success">
                  {pwMsg}
                </div>
              )}
              {pwError && (
                <div className="bg-red-950 border border-red-800 text-red-300 text-sm px-4 py-3 rounded-lg" data-testid="pw-change-error">
                  {pwError}
                </div>
              )}
              <div>
                <label className="block text-zinc-400 text-sm mb-1.5">Current Password</label>
                <input
                  type="password"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1.5">New Password</label>
                <input
                  type="password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1.5">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Re-enter new password"
                  minLength={8}
                  required
                  data-testid="confirm-new-password-input"
                />
              </div>
              <button
                type="submit"
                disabled={pwLoading}
                className="bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 px-8 rounded-lg transition-colors disabled:opacity-50 text-sm"
                data-testid="change-password-submit"
              >
                {pwLoading ? "Updating..." : "Update Password"}
              </button>
            </form>

            <SectionHeader title="Webhook Configuration" />
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-lg">
              <p className="text-zinc-400 text-sm mb-3">Configure this URL in your Resend dashboard to receive email tracking events:</p>
              <code className="block bg-zinc-800 text-amber-400 px-4 py-3 rounded-lg text-sm break-all" data-testid="webhook-url">
                {API}/admin/webhooks/resend
              </code>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
